import { useCallback, useContext, useEffect, useState } from 'react';
import { useBlocker, useInRouterContext, useLocation } from 'react-router';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { hasChangeHistoryParams } from '@gonfalon/change-history/ChangeHistory/ChangeHistoryUtils';
import { isSandbox } from '@gonfalon/constants/constants';
import { restoreEnvCanceledNavigation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ONBOARDING_QUERY_PARAMS } from '@gonfalon/onboarding-guidance/constants';
import { selectedEnvironments } from '@gonfalon/rest-api/queries/selectedEnvironments';
import { RoutePatternContext } from '@gonfalon/router/RoutePatternContext';
import { useQueryClient } from '@tanstack/react-query';
import invariant from 'tiny-invariant';

import {
  trackCanceledNavigationWithUnsavedChanges,
  trackConfirmedNavigationWithUnsavedChanges,
} from './internal/analytics';
import { NavigationPromptModal } from './NavigationPromptModal';

/**
 * Returns true when two search strings are equal after removing the given params from both.
 * Used to compare URLs while ignoring specific query parameters.
 */
function areSearchParamsEqual(currentSearch: string, nextSearch: string, paramsToIgnore: readonly string[]): boolean {
  const currentFiltered = new URLSearchParams(currentSearch);
  const nextFiltered = new URLSearchParams(nextSearch);
  paramsToIgnore.forEach((param) => {
    currentFiltered.delete(param);
    nextFiltered.delete(param);
  });
  return currentFiltered.toString() === nextFiltered.toString();
}

/**
 * Returns true when the only difference between current and next search is onboardingFlow and/or step.
 * Used to allow navigation when the user clicks Next/Back in the onboarding guidance popover.
 */
function isOnboardingStepChangeOnly(currentSearch: string, nextSearch: string): boolean {
  return areSearchParamsEqual(currentSearch, nextSearch, ONBOARDING_QUERY_PARAMS);
}

export type NavigationPromptProps = {
  shouldBlock: boolean;
  allowedPaths?: Set<string | RegExp>;
  allowedQueryParams?: string[];
  onConfirmNavigationCallBack?(): void;
  onCancelNavigationCallBack?(): void;
  /** Called each time the prompt starts blocking a navigation. */
  onBlock?(): void;
  title?: string;
  body?: string;
  confirmText?: string;
  cancelText?: string;
};

export function NavigationPrompt({
  shouldBlock,
  allowedPaths,
  allowedQueryParams,
  onConfirmNavigationCallBack,
  onCancelNavigationCallBack,
  onBlock,
  title,
  body,
  confirmText,
  cancelText,
}: NavigationPromptProps) {
  const isInRouterContext = useInRouterContext();
  invariant(isInRouterContext, 'You should not use <NavigationPrompt> outside of a <Router>');

  const { pathname, search } = useLocation();
  const routePattern = useContext(RoutePatternContext);
  const [action, setAction] = useState<('POP' | 'PUSH' | 'REPLACE') | undefined>(undefined);

  /**
   * Checks if the navigation is only changing allowed query parameters on the same path
   */
  const isAllowedQueryParamChange = (currentSearch: string, nextSearch: string, nextPathname: string) => {
    // Must be same pathname
    if (pathname !== nextPathname) {
      return false;
    }

    if (!allowedQueryParams || allowedQueryParams.length === 0) {
      return false;
    }

    return areSearchParamsEqual(currentSearch, nextSearch, allowedQueryParams);
  };

  const context = useProjectContext({ optional: true });
  const queryClient = useQueryClient();

  const restoreEnvironmentSelector = useCallback(() => {
    if (!context) {
      return;
    }
    const {
      selectedEnvironment: { key: selectedEnvironmentKey },
      environments,
      project,
    } = context;
    const selectedEnvironmentsQuery = selectedEnvironments({ projectKey: project.key });

    queryClient.setQueryData(selectedEnvironmentsQuery.queryKey, (old) => ({
      ...old,
      environments,
      selectedEnvironmentKey,
    }));
  }, [queryClient, context]);
  const blocker = useBlocker(({ historyAction, nextLocation }) => {
    // Never block on the sandbox. It is readonly and we want customers to roam without restrictions.
    if (isSandbox()) {
      blocker.proceed?.();
      return false;
    }

    if (blocker.state === 'blocked' && !shouldBlock) {
      blocker.reset();
      return false;
    }

    const nextPathname = nextLocation.pathname;

    // Check if the UI that initiated the navigation permits certain non-blocking transitions.
    // Example: the user proceeds to checkout after tweaking their plan.
    if (allowedPaths && allowedPaths.size > 0) {
      for (const v of allowedPaths) {
        if (v === nextPathname || (v instanceof RegExp && nextPathname.match(v))) {
          blocker.proceed?.();
          return false;
        }
      }
    }

    // Allow navigation when change history is involved - either opening or closing the modal
    // This handles both directions: navigating TO change history (opening) and FROM change history (closing)
    const currentSearchParams = new URLSearchParams(search);
    const nextSearchParams = new URLSearchParams(nextLocation.search);
    const currentHasChangeHistory = hasChangeHistoryParams(currentSearchParams);
    const nextHasChangeHistory = hasChangeHistoryParams(nextSearchParams);

    if (currentHasChangeHistory || nextHasChangeHistory) {
      blocker.proceed?.();
      return false;
    }

    // Allow navigation if only allowed query parameters are changing
    if (isAllowedQueryParamChange(search, nextLocation.search, nextPathname)) {
      blocker.proceed?.();
      return false;
    }

    // Allow navigation when only onboarding guidance step is changing (Next/Back in the guidance popover).
    // Same pathname and the only query param changes are onboardingFlow and/or step.
    if (pathname === nextPathname && isOnboardingStepChangeOnly(search, nextLocation.search)) {
      blocker.proceed?.();
      return false;
    }

    // Show the confirmation dialog, remember the action type so that we can
    // forward it along with analytics
    if (shouldBlock) {
      setAction(historyAction);
    }

    return shouldBlock;
  });
  const isVisible = blocker.state === 'blocked';

  useEffect(() => {
    if (isVisible) {
      onBlock?.();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- one report per block, even if the callback identity changes
  }, [isVisible]);
  // Wire up a `beforeunload` handler, because `useBlocker` doesn't do this for us and we want to preserve the original
  // behavior that was implemented via `history.block`:
  // - The native prompt should appear if the form is dirty and the user attempts a hard refresh (e.g. Cmd+R on a Mac)
  // - The native prompt can be opened on top of the custom prompt in NavigationPromptModal, if the form is in a dirty
  //   state, then the user attempts to navigate away, and then the user attempts to hard refresh before interacting
  //   with the custom prompt
  // - If the native prompt is shown on top of the custom prompt and then cancelled, the custom prompt still requires
  //   user interaction
  useEffect(() => {
    const handler = (e: Event) => {
      // Again like in useBlocker, never block on the sandbox.
      if (isSandbox()) {
        return;
      }

      if (shouldBlock) {
        e.preventDefault();
        // @ts-expect-error this is the intended use https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
        e.returnValue = ''; // oxlint-disable-line no-param-reassign

        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handler, { capture: true });

    return () => {
      window.removeEventListener('beforeunload', handler, { capture: true });
    };
  }, [shouldBlock]);

  return isVisible ? (
    <NavigationPromptModal
      onCancelNavigation={() => {
        trackCanceledNavigationWithUnsavedChanges(routePattern, action);
        if (onCancelNavigationCallBack) {
          onCancelNavigationCallBack();
        }
        if (restoreEnvCanceledNavigation()) {
          restoreEnvironmentSelector();
        }
        blocker.reset?.();
      }}
      onConfirmNavigation={() => {
        trackConfirmedNavigationWithUnsavedChanges(routePattern, action);
        if (onConfirmNavigationCallBack) {
          onConfirmNavigationCallBack();
        }
        blocker.proceed?.();
      }}
      title={title}
      body={body}
      confirmText={confirmText}
      cancelText={cancelText}
    />
  ) : null;
}
